import { computed, useRoute } from '~/utils/nuxt3-migration'
import { getRouteMetaItem } from '~/utils/router'
import { ANON_GUARD_COOKIE } from '~/constants/anon-guard'
import { useCookies } from '~/compositions/useCookies'
import { useNamespacedStore } from '~/compositions/store'
import { PARKING_NS, ParkingState } from '~/store/modules/shared/parking/state'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import useRuntimeConfig from '~/compositions/useRuntimeConfig'
import { useUserAgent } from '~/compositions/user-agent'
import { useDeps } from '~/compositions/dependency-container'
import DealerSiteService from '~/services/dealers/site/DealerSiteService'
import {
  LOGIN_MODAL_NS,
  LoginModalState
} from '~/store/modules/shared/login-modal/state'

export function useGlobalElements() {
  const route = useRoute()
  const cookies = useCookies()
  const { isMobile } = useUserAgent()
  const [dealerSiteService] = useDeps(DealerSiteService)

  const { state: parkingState } = useNamespacedStore<ParkingState>(PARKING_NS)

  const { getters: userGetters, state: userState } = useNamespacedStore<
    UserState
  >(USER_NS)
  const {
    public: { googleOauthClientId }
  } = useRuntimeConfig()

  const { state: loginModalState } = useNamespacedStore<LoginModalState>(
    LOGIN_MODAL_NS
  )

  const isLoginModalVisible = computed(
    () => loginModalState.isLoginModalVisible
  )

  const isSingleOrAnon = computed(() => userGetters('isSingleOrAnon'))

  const inTrades = computed(() => {
    return getRouteMetaItem(route.value, 'inTrades')
  })

  const isLanding = computed(() => {
    return getRouteMetaItem(route.value, 'isLanding')
  })

  const isIframe = computed(() => {
    return getRouteMetaItem(route.value, 'isIframe')
  })

  const isSubdomain = computed(() => {
    return (
      getRouteMetaItem(route.value, 'isSubdomain') ||
      route.value.name?.startsWith('__subdomain')
    )
  })

  const isDealerSite = computed(() => {
    return dealerSiteService.routeIsOfDealerSite()
  })

  const inClassifiedView = computed(() => {
    return route.value.name?.startsWith('__classifieds_view')
  })

  const isNoHeaderRoute = computed(() => {
    const noHeaderMetaItem = getRouteMetaItem(route.value, 'noHeader')

    if (!noHeaderMetaItem) {
      return false
    }

    if (typeof noHeaderMetaItem === 'boolean') {
      return noHeaderMetaItem
    }

    return noHeaderMetaItem === (isMobile.value ? 'mobile' : 'desktop')
  })

  const isNoFooterRoute = computed(() => {
    const noFooterMetaItem = getRouteMetaItem(route.value, 'noFooter')

    if (!noFooterMetaItem) {
      return false
    }

    if (typeof noFooterMetaItem === 'boolean') {
      return noFooterMetaItem
    }

    return noFooterMetaItem === (isMobile.value ? 'mobile' : 'desktop')
  })

  const showFooter = computed(() => {
    return (
      !isIframe.value &&
      !isSubdomain.value &&
      !isDealerSite.value &&
      !isNoFooterRoute.value
    )
  })

  const showHeader = computed(() => {
    return (
      !isIframe.value &&
      !isSubdomain.value &&
      !isDealerSite.value &&
      !isNoHeaderRoute.value
    )
  })

  const showMobileBottomMenu = computed(() => {
    return (
      !isIframe.value &&
      !isSubdomain.value &&
      !isDealerSite.value &&
      !inTrades.value &&
      !isLanding.value &&
      !inClassifiedView.value &&
      !inMap.value &&
      !getRouteMetaItem(route.value, 'hideMobileBottomMenu')
    )
  })

  const showParking = computed(() => parkingState.showParking)

  const inMap = computed(() => {
    return getRouteMetaItem(route.value, 'inMap')
  })

  const showGlobalAlerts = computed(() => {
    return (
      !isIframe.value &&
      !isSubdomain.value &&
      !isDealerSite.value &&
      !inTrades.value &&
      !isLanding.value &&
      !(inMap.value && isMobile.value) &&
      !isNoFooterRoute.value &&
      !isNoHeaderRoute.value
    )
  })

  const showGoogleOneTap = computed(() => {
    if (
      googleOauthClientId &&
      isSingleOrAnon.value &&
      !route.value.query.relatedDealer
    ) {
      return (
        getRouteMetaItem(route.value, 'showOneTap') ||
        (getRouteMetaItem(route.value, 'showOneTapIfNoAnonModal') &&
          cookies.get(ANON_GUARD_COOKIE))
      )
    }
    return false
  })

  const showAccountIndicator = computed(() => {
    const unreadCounts = [
      userState.unreadMessages,
      userState.unreadNotifications,
      userState.unreadParkingNotifications,
      userState.unreadTradesNotifications,
      userState.unreadAnnouncements,
      userState.rogueClassifiedsCount,
      userGetters('marketplaceSellerNewOrdersCount')
    ]
    return unreadCounts.some(count => count > 0)
  })

  return {
    isIframe,
    showMobileBottomMenu,
    isSubdomain,
    showParking,
    showFooter,
    showHeader,
    showGoogleOneTap,
    showGlobalAlerts,
    inMap,
    isLoginModalVisible,
    showAccountIndicator
  }
}
